import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiVoiceGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-voice-generator',
			name: 'AI Voice Generator — Turn Your Script into Lifelike Narration',
			description:
				"Create a natural-sounding voice over for videos, lessons, and ads with Lift's AI voice maker. Choose a language and voice, generate, and download the audio. Start now.",
			url: 'https://liftapp.ai/ai-voice-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-voice-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-voice-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-voice-generator#product',
			name: 'AI Voice Generator',
			description:
				'Type your script, pick a language and one of many realistic AI voices, then download a ready-to-use voiceover. Create yours now.',
			url: 'https://liftapp.ai/ai-voice-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-voice-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Type or Paste Your Script',
					text: 'Enter the text you want voiced, from a short line to a full paragraph of narration.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose a Language and Voice',
					text: 'Pick a language, then preview voices by name and mood to find the right fit for your script.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate Your AI Voiceover',
					text: 'Tap Generate and Lift voices your script with the language and speaker you selected.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Play, Download, or Regenerate',
					text: 'Listen back in My voiceovers, download the audio, or open a result to regenerate it.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-voice-generator#benefits',
			name: 'Who can benefit from an AI voice generator?',
			description:
				'Lift’s AI Voice Generator helps creators, educators, and businesses turn written scripts into natural-sounding voiceovers for videos, lessons, ads, and more.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Short-video creators — Add a polished voice over to reels and shorts by typing the lines instead of recording them.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: "Online educators — Narrate lesson intros and explainer clips in your learners' language by switching the language setting.",
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Voice product promos and ad spots with professional AI voices, without booking studio time or talent.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Wellness creators — Turn breathing exercises and meditation scripts into guided audio using a calm, warm voice.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Language learners — Hear how phrases sound by typing them, choosing the target language, and replaying the result.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/images/ai-voice-generator/benefits.webp',
				name: 'AI Voice Generator by Lift',
				description:
					'Laptop with a voice AI tool on screen, a played voiceover, headphones, and books on a sunny desk',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-voice-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Voice Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift AI Voice Generator turns written text into spoken audio in the language and voice you choose. Use it to create voiceovers for short clips, lessons, promos, and personal projects.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Voice Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Type or paste your script, then choose a language and preview the voices to pick one. Tap Generate, and your voiceover appears in My voiceovers, ready to play, download, or regenerate.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I hear a voice before generating my voiceover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — every voice has a short audio preview that matches the language you select. Each voice also shows a mood description, so you can match its tone to your script.',
					},
				},
				{
					'@type': 'Question',
					name: 'What languages can I create a voiceover in?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift supports a wide range of languages — pick one from the popular list or search the full list by name. For the most natural result, write your script in the language you select.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose between male and female voices?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — filter the voice list by gender or browse all voices at once. Pick the one whose tone suits your script, and switch to another anytime for your next voiceover.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-voice-generator#why-lift',
			name: 'Why choose Lift for AI voices',
			description:
				'Lift’s AI Voice Generator combines many languages, distinct speakers, and saved voiceovers in one affordable tool available on iOS, Android, and Web.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-voice-generator/whyLift.webp',
				name: 'Why use Lift AI Voice Generator',
				description:
					'My voiceovers card with a meditation voice over playing, language list, and voice options with mood labels',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-voice-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-voice-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Voice Generator',
					item: 'https://liftapp.ai/ai-voice-generator',
				},
			],
		},
	],
};
